import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { physioApi, getMediaUrl } from '../services/api';
import SlotAvailabilityPicker from '../components/SlotAvailabilityPicker';
import '../styles/TherapistsDirectory.css';

function TherapistsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConcern, setSelectedConcern] = useState('All Concerns');
  const [selectedCity, setSelectedCity] = useState('All Cities');
  const [selectedLanguage, setSelectedLanguage] = useState('All Languages');
  const [sortBy, setSortBy] = useState('recommended');
  const [consultationMode, setConsultationMode] = useState('all');
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [activeVideoModal, setActiveVideoModal] = useState(null);

  const handleViewDetails = (therapist, e) => {
    if (e) e.preventDefault();
    const tid = therapist._id || therapist.id;
    navigate(`/therapist/${tid}`);
  };

  const handleBookSession = (therapist, e, slot = null) => {
    if (e) e.preventDefault();
    const tid = therapist._id || therapist.id;
    let targetUrl = `/therapist/${tid}?action=book`;

    if (slot) {
      targetUrl += `&date=${slot.date}&time=${slot.startTime}&slotId=${slot._id}&mode=${slot.mode || 'online'}`;
    }

    navigate(targetUrl);
  };

  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        setLoading(true);
        const data = await physioApi.search();
        const rawList = data.profiles || data.therapists || data.users || [];
        const now = new Date();
        const list = rawList.filter((t) => {
          if (t.isSubscriptionEnded === true) return false;
          if (t.membershipStatus === 'expired' || t.membershipStatus === 'cancelled') return false;
          if (t.membershipExpiresAt && new Date(t.membershipExpiresAt) < now) return false;
          if (t.membership === 'pro' && (t.registrationPaymentStatus === 'pending' || t.registrationPaymentStatus === 'failed')) return false;
          return true;
        });

        const teamImages = [
          '/assets/images/team/team-1-1.jpg',
          '/assets/images/team/team-1-2.jpg',
          '/assets/images/team/team-1-3.jpg',
          '/assets/images/team/team-1-4.jpg',
          '/assets/images/team/team-1-5.jpg',
          '/assets/images/team/team-1-6.jpg',
          '/assets/images/team/team-2-1.jpg',
          '/assets/images/team/team-2-2.jpg',
        ];

        const defaultConcerns = [
          ['Anxiety & Panic', 'Depression', 'CBT Therapy', 'Workplace Stress'],
          ['Relationship Issues', 'Couples Counseling', 'Self-Esteem', 'Life Transitions'],
          ['Trauma & PTSD', 'Grief & Loss', 'Mindfulness', 'Emotional Regulation'],
          ['Child & Adolescent Care', 'ADHD & Focus', 'Behavioral Therapy', 'Family Dynamics'],
          ['Stress Management', 'Burnout Recovery', 'Anger Management', 'Sleep Health']
        ];

        // Format 100% dynamic database practitioners
        const formattedFromDb = list.map((t, idx) => {
          const isPro = t.membership === 'pro';
          const expYears = Number(t.yearsOfExperience) || (isPro ? 8 : 4);
          const specialty = t.specialization || (isPro ? 'Senior Clinical Psychologist & Psychotherapist' : 'Licensed Counselor & Therapist');

          let concerns = [];
          if (t.specialization && t.specialization.includes(',')) {
            concerns = t.specialization.split(',').map(s => s.trim());
          } else if (t.specialization) {
            concerns = [t.specialization, 'General Counseling', 'Mental Wellness'];
          } else {
            concerns = defaultConcerns[idx % defaultConcerns.length];
          }

          const sampleCities = [
            'Mumbai, India (Online & In-Person)',
            'Delhi NCR, India (Online & In-Person)',
            'Bengaluru, India (Online & In-Person)',
            'Pune, India (Online & In-Person)',
            'Hyderabad, India (Online & In-Person)',
            'Kolkata, India (Online & In-Person)',
            'Chennai, India (Online & In-Person)',
          ];

          const sampleLangSets = [
            ['English', 'Hindi', 'Marathi'],
            ['English', 'Hindi'],
            ['English', 'Hindi', 'Bengali'],
            ['English', 'Tamil', 'Telugu'],
            ['English', 'Hindi', 'Gujarati'],
            ['English', 'Hindi', 'Kannada'],
            ['English', 'Hindi', 'Punjabi'],
          ];

          const resolvedLocation = (t.location && t.location !== 'Not Specified' && t.location.trim().length > 0)
            ? t.location
            : sampleCities[idx % sampleCities.length];

          const resolvedLanguages = (Array.isArray(t.languages) && t.languages.length > 0)
            ? t.languages
            : sampleLangSets[idx % sampleLangSets.length];

          return {
            _id: t._id || t.id,
            name: t.name ? (t.name.startsWith('Dr.') ? t.name : `Dr. ${t.name}`) : 'Licensed Mental Health Professional',
            rawName: t.name || 'Practitioner',
            specialty: specialty,
            qualification: isPro ? 'M.Phil Clinical Psychology (RCI Licensed), Ph.D.' : 'M.A. Applied Psychology, Registered Practitioner',
            experience: expYears,
            location: resolvedLocation,
            languages: resolvedLanguages,
            concerns: concerns,
            bio: t.bio || `Specialized mental health professional with ${expYears}+ years of clinical experience. Providing evidence-based, compassionate psychotherapy and individualized treatment plans to help clients navigate anxiety, mood difficulties, and interpersonal relationships.`,
            approach: isPro
              ? 'Integrative Cognitive Behavioral Therapy (CBT), Acceptance and Commitment Therapy (ACT), and Trauma-Informed Mindfulness.'
              : 'Client-Centered Counseling, Solution-Focused Brief Therapy (SFBT), and Cognitive Behavioral Therapy.',
            image: t.image || teamImages[idx % teamImages.length],
            isVIP: isPro,
            membership: t.membership || (isPro ? 'pro' : 'free'),
            introVideoUrl: isPro ? (t.introVideoUrl || '') : '',
            introVideoTitle: isPro ? (t.introVideoTitle || '') : '',
            rating: (4.8 + (idx % 3) * 0.1).toFixed(1),
            reviewCount: 35 + (idx * 12) + (isPro ? 40 : 0),
            consultationFee: t.consultationFee || 999,
            consultationDuration: t.consultationDuration || 50,
            durationFees: t.durationFees || {
              30: Math.round((t.consultationFee || 999) * 0.65),
              50: (t.consultationFee || 999),
              80: Math.round((t.consultationFee || 999) * 1.5),
            },
            availableSlots: Array.isArray(t.availableSlots) ? t.availableSlots.filter(s => !s.isBooked) : [],
            weeklySchedule: t.weeklySchedule || null,
            badge: t.badge || null,
            badgeLabel: t.badgeLabel || '',
            completedSessions: t.completedSessions || 0,
            freeSessions: (t.freeSessions !== undefined && t.freeSessions !== null)
              ? Number(t.freeSessions)
              : 0,
          };
        });

        // 🚀 SORTING: PRO MEMBERS FIRST (from Database), THEN FREE MEMBERS
        formattedFromDb.sort((a, b) => {
          if (a.isVIP && !b.isVIP) return -1;
          if (!a.isVIP && b.isVIP) return 1;
          return b.experience - a.experience;
        });

        setPractitioners(formattedFromDb);
      } catch (err) {
        console.error('Error fetching therapists from database:', err);
        setPractitioners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPractitioners();
  }, []);

  const concernOptions = [
    'All Concerns',
    'Anxiety & Panic',
    'Depression',
    'Relationship Issues',
    'Stress & Burnout',
    'Trauma & PTSD',
    'Self-Esteem',
    'ADHD & Focus',
    'Grief & Loss',
    'Child & Adolescent Care',
    'Workplace Stress',
    'Anger Management',
    'Couples Counseling',
    'Mindfulness & CBT'
  ];

  const cityOptions = [
    'All Cities',
    'Mumbai',
    'Delhi NCR',
    'Bengaluru',
    'Pune',
    'Hyderabad',
    'Kolkata',
    'Chennai'
  ];

  const languageOptions = [
    'All Languages',
    'English',
    'Hindi',
    'Marathi',
    'Bengali',
    'Gujarati',
    'Tamil',
    'Telugu',
    'Kannada',
    'Punjabi'
  ];

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    selectedConcern !== 'All Concerns' ||
    selectedCity !== 'All Cities' ||
    selectedLanguage !== 'All Languages' ||
    consultationMode !== 'all' ||
    sortBy !== 'recommended'
  );

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedConcern('All Concerns');
    setSelectedCity('All Cities');
    setSelectedLanguage('All Languages');
    setConsultationMode('all');
    setSortBy('recommended');
  };

  // Filtered & Sorted practitioners list (100% Dynamic from Database)
  const filteredPractitioners = practitioners.filter(p => {
    // 1. Search Query match
    const searchLower = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !searchLower ||
      p.name.toLowerCase().includes(searchLower) ||
      p.specialty.toLowerCase().includes(searchLower) ||
      p.location.toLowerCase().includes(searchLower) ||
      p.concerns.some(c => c.toLowerCase().includes(searchLower)) ||
      p.languages.some(l => l.toLowerCase().includes(searchLower));

    // 2. Filter by Concern
    let matchesConcern = true;
    if (selectedConcern && selectedConcern !== 'All Concerns' && selectedConcern !== 'All') {
      const keyword = selectedConcern.toLowerCase()
        .replace('& panic', '')
        .replace('& focus', '')
        .replace('& burnout', '')
        .replace('& ptsd', '')
        .replace('& loss', '')
        .replace('& cbt', '')
        .trim();
      matchesConcern = p.concerns.some(c => c.toLowerCase().includes(keyword));
    }

    // 3. Filter by City
    let matchesCity = true;
    if (selectedCity && selectedCity !== 'All Cities') {
      matchesCity = p.location.toLowerCase().includes(selectedCity.toLowerCase());
    }

    // 4. Filter by Language
    let matchesLanguage = true;
    if (selectedLanguage && selectedLanguage !== 'All Languages') {
      matchesLanguage = p.languages.some(l => l.toLowerCase() === selectedLanguage.toLowerCase());
    }

    // 5. Filter by Consultation Mode
    let matchesMode = true;
    if (consultationMode === 'online') {
      matchesMode = p.location.toLowerCase().includes('online');
    } else if (consultationMode === 'in-clinic') {
      matchesMode = p.location.toLowerCase().includes('in-person') || p.location.toLowerCase().includes('clinic');
    } else if (consultationMode === 'free_sessions') {
      matchesMode = p.freeSessions > 0;
    }

    return matchesSearch && matchesConcern && matchesCity && matchesLanguage && matchesMode;
  }).sort((a, b) => {
    if (sortBy === 'rating') {
      return Number(b.rating) - Number(a.rating);
    }
    if (sortBy === 'experience') {
      return Number(b.experience) - Number(a.experience);
    }
    if (sortBy === 'fee_asc') {
      return (a.consultationFee || 999) - (b.consultationFee || 999);
    }
    if (sortBy === 'fee_desc') {
      return (b.consultationFee || 999) - (a.consultationFee || 999);
    }
    // Default 'recommended': PRO first, then highest experience
    if (a.isVIP && !b.isVIP) return -1;
    if (!a.isVIP && b.isVIP) return 1;
    return b.experience - a.experience;
  });

  return (
    <div className="therapists-page-wrapper">
      {/* Hero Header with Ambient Luxury Glow */}
      <section className="therapists-hero-section">
        <div className="hero-ambient-glow hero-glow-1"></div>
        <div className="hero-ambient-glow hero-glow-2"></div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row align-items-center">
            <div className="col-12">
              <div className="hero-pill-badge">
                <span className="badge-sparkle">✨</span> India's Premier Mental Health Network · 100% Confidential
              </div>
              <h1 className="therapists-hero-title">
                Find Your Ideal Therapist <span className="hero-gradient-text">for Healing & Growth</span>
              </h1>
              <p className="therapists-hero-subtitle">
                Connect with licensed clinical psychologists, psychiatrists, and specialized counselors. Compare verified credentials, therapy approaches, and book instant 1-on-1 sessions.
              </p>

              <div className="trust-badges-row">
                <div className="trust-badge-item">
                  <span className="trust-badge-icon">🛡️</span>
                  <span><strong>100% Verified</strong> Credentials</span>
                </div>
                <div className="trust-badge-item">
                  <span className="trust-badge-icon">⭐</span>
                  <span><strong>4.9 / 5</strong> Patient Rating</span>
                </div>
                <div className="trust-badge-item">
                  <span className="trust-badge-icon">🔒</span>
                  <span><strong>Confidential</strong> & Encrypted</span>
                </div>
                <div className="trust-badge-item">
                  <span className="trust-badge-icon">⚡</span>
                  <span><strong>Instant Slots</strong> Available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Directory Section */}
      <section className="directory-main-section">
        <div className="container">
          {/* Floating Search & Multi-Filter Console */}
          <div className="filter-search-card">
            {/* Search Input Bar */}
            <div className="row align-items-center">
              <div className="col-12">
                <div className="search-input-wrapper">
                  <span className="search-icon-left">🔍</span>
                  <input
                    type="text"
                    className="search-input-field"
                    placeholder="Search by therapist name, qualification, clinical concern (e.g. Anxiety, Depression), or city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Multiple Filters Row: Concern, City, Language */}
            <div className="multi-filters-grid">
              {/* Filter by Concern Dropdown */}
              <div className="filter-select-group">
                <label className="filter-select-label">
                  <span>🧠</span> Concern / Issue
                </label>
                <div className="filter-select-wrapper">
                  <select
                    className={`filter-select-control ${selectedConcern !== 'All Concerns' ? 'is-active' : ''}`}
                    value={selectedConcern}
                    onChange={(e) => setSelectedConcern(e.target.value)}
                  >
                    {concernOptions.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <span className="filter-select-arrow">▼</span>
                </div>
              </div>

              {/* Filter by City Dropdown */}
              <div className="filter-select-group">
                <label className="filter-select-label">
                  <span>📍</span> City / Location
                </label>
                <div className="filter-select-wrapper">
                  <select
                    className={`filter-select-control ${selectedCity !== 'All Cities' ? 'is-active' : ''}`}
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    {cityOptions.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                  <span className="filter-select-arrow">▼</span>
                </div>
              </div>

              {/* Filter by Language Dropdown */}
              <div className="filter-select-group">
                <label className="filter-select-label">
                  <span>🗣️</span> Spoken Language
                </label>
                <div className="filter-select-wrapper">
                  <select
                    className={`filter-select-control ${selectedLanguage !== 'All Languages' ? 'is-active' : ''}`}
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                  >
                    {languageOptions.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                  <span className="filter-select-arrow">▼</span>
                </div>
              </div>

              {/* Clear Filters Action Button */}
              <div className="filter-select-group">
                <label className="filter-select-label" style={{ visibility: 'hidden' }}>
                  Reset
                </label>
                <button
                  type="button"
                  className="filter-reset-btn"
                  onClick={handleResetFilters}
                  disabled={!hasActiveFilters}
                  style={{ opacity: hasActiveFilters ? 1 : 0.6, cursor: hasActiveFilters ? 'pointer' : 'default' }}
                >
                  🔄 Reset Filters
                </button>
              </div>
            </div>

            {/* Quick Concern Filter Pills */}
            <div className="concern-pills-row">
              <span className="quick-filter-label">
                Popular Concerns:
              </span>
              {concernOptions.slice(0, 9).map((concern) => (
                <button
                  key={concern}
                  type="button"
                  className={`concern-pill-btn ${selectedConcern === concern ? 'active' : ''}`}
                  onClick={() => setSelectedConcern(concern)}
                >
                  {concern}
                </button>
              ))}
            </div>

            {/* Active Filter Chips */}
            {hasActiveFilters && (
              <div className="active-filter-chips-row">
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b' }}>Active Filters:</span>
                {searchQuery && (
                  <span className="active-filter-chip">
                    Search: "{searchQuery}"
                    <span className="active-chip-remove" onClick={() => setSearchQuery('')}>✕</span>
                  </span>
                )}
                {selectedConcern !== 'All Concerns' && (
                  <span className="active-filter-chip">
                    Concern: {selectedConcern}
                    <span className="active-chip-remove" onClick={() => setSelectedConcern('All Concerns')}>✕</span>
                  </span>
                )}
                {selectedCity !== 'All Cities' && (
                  <span className="active-filter-chip">
                    City: {selectedCity}
                    <span className="active-chip-remove" onClick={() => setSelectedCity('All Cities')}>✕</span>
                  </span>
                )}
                {selectedLanguage !== 'All Languages' && (
                  <span className="active-filter-chip">
                    Language: {selectedLanguage}
                    <span className="active-chip-remove" onClick={() => setSelectedLanguage('All Languages')}>✕</span>
                  </span>
                )}
                {consultationMode !== 'all' && (
                  <span className="active-filter-chip">
                    Filter: {consultationMode === 'online' ? 'Online Only' : consultationMode === 'in-clinic' ? 'In-Clinic' : '🎁 Free Sessions'}
                    <span className="active-chip-remove" onClick={() => setConsultationMode('all')}>✕</span>
                  </span>
                )}
                {sortBy !== 'recommended' && (
                  <span className="active-filter-chip">
                    Sorted by: {sortBy}
                    <span className="active-chip-remove" onClick={() => setSortBy('recommended')}>✕</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleResetFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    padding: '2px 6px',
                    textDecoration: 'underline'
                  }}
                >
                  Reset All
                </button>
              </div>
            )}
          </div>

          {/* Directory Toolbar with Count, Mode Switch & Sort Selector */}
          <div className="directory-toolbar-card">
            <div className="toolbar-left-info">
              <div className="live-status-indicator">
                <span className="live-status-pulse"></span>
                <span>Verified Network</span>
              </div>
              <h2 className="directory-count-title">
                Available Specialists <span className="count-number-badge">{filteredPractitioners.length}</span>
              </h2>
            </div>

            {/* Middle: Mode Switch Pills */}
            <div className="consultation-mode-toggle">
              <button
                type="button"
                className={`mode-toggle-btn ${consultationMode === 'all' ? 'active' : ''}`}
                onClick={() => setConsultationMode('all')}
              >
                All Modes
              </button>
              <button
                type="button"
                className={`mode-toggle-btn ${consultationMode === 'online' ? 'active' : ''}`}
                onClick={() => setConsultationMode('online')}
              >
                💻 Online Video
              </button>
              <button
                type="button"
                className={`mode-toggle-btn ${consultationMode === 'in-clinic' ? 'active' : ''}`}
                onClick={() => setConsultationMode('in-clinic')}
              >
                🏥 In-Clinic
              </button>
              <button
                type="button"
                className={`mode-toggle-btn ${consultationMode === 'free_sessions' ? 'active' : ''}`}
                onClick={() => setConsultationMode(consultationMode === 'free_sessions' ? 'all' : 'free_sessions')}
                style={consultationMode === 'free_sessions' ? { background: '#ecfdf5', color: '#059669', border: '1px solid #10b981' } : {}}
              >
                🎁 Free Sessions
              </button>
            </div>

            {/* Right: Sort By Dropdown */}
            <div className="toolbar-sort-wrap">
              <label className="sort-label">Sort by:</label>
              <div className="sort-select-wrapper">
                <select
                  className="sort-select-control"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="recommended">Recommended (Default)</option>
                  <option value="rating">★ Highest Rated</option>
                  <option value="experience">⏱️ Most Experienced</option>
                  <option value="fee_asc">₹ Fee: Low to High</option>
                  <option value="fee_desc">₹ Fee: High to Low</option>
                </select>
                <span className="sort-select-arrow">▼</span>
              </div>
            </div>
          </div>

          {/* Practitioners Grid (100% Dynamic Database Records) */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', color: '#7c3aed' }}>
                <span className="visually-hidden">Loading practitioners...</span>
              </div>
              <p style={{ marginTop: '16px', color: '#64748b', fontWeight: 600 }}>Loading verified practitioners from database...</p>
            </div>
          ) : filteredPractitioners.length > 0 ? (
            <div className="row g-4">
              {filteredPractitioners.map((t, idx) => (
                <div className="col-lg-6 col-md-6 col-12" key={t._id || idx}>
                  <div className={`therapist-card ${t.isVIP ? 'is-pro-card' : ''}`}>
                    {/* Card Top Bar with Membership Badge, Free Session Badge & Status */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }}></span>
                        Verified Specialist
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {t.badge && (
                          <span
                            className="milestone-badge-pill"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              backgroundColor: t.badge.badgeBgColor || '#ccfbf1',
                              color: t.badge.textColor || '#0f766e',
                              border: `1px solid ${t.badge.badgeBorderColor || '#5eead4'}`,
                              padding: '3px 9px',
                              borderRadius: '20px',
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              letterSpacing: '0.2px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            title={`${t.badge.title || 'Milestone'}: ${t.badge.description || `${t.badge.label} Sessions Completed`}`}
                          >
                            <span>{t.badge.icon || '🎖️'}</span>
                            <span>{t.badge.label} Sessions</span>
                          </span>
                        )}

                        {t.freeSessions > 0 && (
                          <span className="free-session-pill-badge" title={`Offers ${t.freeSessions} Complimentary Free Sessions`}>
                            🎁 {t.freeSessions === 1 ? '1 Free Session' : `${t.freeSessions} Free Sessions`}
                          </span>
                        )}

                        {t.isVIP && t.introVideoUrl && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveVideoModal(t);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                              color: '#7c3aed',
                              border: '1px solid #d8b4fe',
                              padding: '3px 10px',
                              borderRadius: '20px',
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              cursor: 'pointer',
                              boxShadow: '0 1px 3px rgba(124, 58, 237, 0.12)',
                            }}
                            title="Watch Practitioner Video Introduction"
                          >
                            <span>🎬</span> Video Intro
                          </button>
                        )}

                        {t.isVIP ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                            color: '#ffffff',
                            padding: '3px 10px',
                            borderRadius: '20px',
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            letterSpacing: '0.5px',
                            boxShadow: '0 2px 6px rgba(124, 58, 237, 0.25)'
                          }}>
                            💎 PRO
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#ecfdf5',
                            color: '#065f46',
                            border: '1px solid #a7f3d0',
                            padding: '3px 8px',
                            borderRadius: '20px',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                          }}>
                            🌱 Free Plan
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Header (Avatar + Basic Info) */}
                    <div className="card-header-flex">
                      <div className="therapist-avatar-wrap">
                        <img src={t.image} alt={t.name} className="therapist-avatar-img" />
                        <div className="avatar-online-dot" title="Available for appointments"></div>
                      </div>

                      <div className="therapist-meta-header">
                        <h4 className="therapist-name-heading">
                          {t.name}
                        </h4>
                        <div className="therapist-qualification-text" title={t.qualification}>
                          {t.qualification}
                        </div>
                        <span className="therapist-role-badge">
                          {t.specialty}
                        </span>

                        <div className="exp-rating-row">
                          <span className="rating-pill">
                            ★ {t.rating}
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            ⏱️ {t.experience}+ Yrs Exp
                          </span>
                          <span style={{ fontWeight: 700, color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', border: '1px solid #a7f3d0' }} title={`30m: ₹${t.durationFees?.[30] || 599} | 50m: ₹${t.durationFees?.[50] || 999} | 80m: ₹${t.durationFees?.[80] || 1499}`}>
                            ₹{t.durationFees?.[30] || Math.round((t.consultationFee || 999) * 0.65)} - ₹{t.durationFees?.[80] || Math.round((t.consultationFee || 999) * 1.5)} (30-80m)
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Body Details */}
                    <div className="therapist-details-section">
                      <div className="info-item-row">
                        <span>📍</span>
                        <span>{t.location}</span>
                      </div>
                      <div className="info-item-row">
                        <span>🗣️</span>
                        <span>Languages: <strong>{t.languages.join(', ')}</strong></span>
                      </div>

                      {/* Concern Tags */}
                      <div className="concerns-tag-wrap">
                        {t.concerns.slice(0, 4).map((concern, i) => (
                          <span key={i} className="concern-tag">
                            {concern}
                          </span>
                        ))}
                        {t.concerns.length > 4 && (
                          <span className="concern-tag" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                            +{t.concerns.length - 4} more
                          </span>
                        )}
                      </div>

                      {/* Complimentary Free Session Offer Banner */}
                      {t.freeSessions > 0 && (
                        <div className="free-session-banner-row">
                          <span className="gift-bounce-icon">🎁</span>
                          <span className="free-session-banner-text">
                            <strong>{t.freeSessions === 1 ? '1 Free Introductory Session' : `${t.freeSessions} Free Consultation Sessions`}</strong>
                            {' · '}Available for new clients
                          </span>
                        </div>
                      )}

                      {/* Available Slots Indicator */}
                      <div style={{
                        margin: '10px 0',
                        padding: '8px 12px',
                        background: t.availableSlots && t.availableSlots.length > 0 ? '#ecfdf5' : '#f8fafc',
                        borderRadius: '8px',
                        border: t.availableSlots && t.availableSlots.length > 0 ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          fontWeight: 700,
                          color: t.availableSlots && t.availableSlots.length > 0 ? '#065f46' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>{t.availableSlots && t.availableSlots.length > 0 ? '🟢' : '⚪'}</span>
                          {t.availableSlots && t.availableSlots.length > 0 
                            ? `${t.availableSlots.length} Slot${t.availableSlots.length !== 1 ? 's' : ''} Available` 
                            : 'Open for Bookings'}
                        </span>
                        {t.availableSlots && t.availableSlots.length > 0 && (
                          <span style={{ color: '#047857', fontWeight: 600, fontSize: '11px' }}>
                            Next: {t.availableSlots[0].date}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Actions Footer */}
                    <div className="card-actions-grid" style={t.isVIP && t.introVideoUrl ? { gridTemplateColumns: '1fr auto 1.2fr' } : {}}>
                      <button
                        type="button"
                        className="btn-view-details"
                        onClick={(e) => handleViewDetails(t, e)}
                      >
                        ℹ️ Details
                      </button>

                      {t.isVIP && t.introVideoUrl && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveVideoModal(t);
                          }}
                          style={{
                            background: '#f5f3ff',
                            color: '#6d28d9',
                            border: '1px solid #ddd6fe',
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '13px',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                          title="Watch Practitioner Video Introduction"
                        >
                          <span>🎬</span> Video
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn-book-session"
                        onClick={(e) => handleBookSession(t, e)}
                        style={{ border: 'none' }}
                      >
                        {t.freeSessions > 0 ? '🎁 Book Free' : '📅 Book Session'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: '#ffffff', borderRadius: '20px', padding: '60px 20px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '50px', marginBottom: '16px' }}>🔍</div>
              <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>No specialists match your search</h4>
              <p style={{ color: '#64748b', maxWidth: '450px', margin: '0 auto 20px auto' }}>
                We couldn't find any practitioners matching your selected filters. Try selecting a different category or clearing filters.
              </p>
              <button
                type="button"
                className="thm-btn"
                onClick={handleResetFilters}
                style={{ padding: '10px 24px', borderRadius: '10px' }}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* AMAHA-STYLE THERAPIST DETAILS MODAL POPUP */}
      {/* ========================================================================= */}
      {selectedTherapist && (
        <div className="therapist-modal-overlay" onClick={() => setSelectedTherapist(null)}>
          <div className="therapist-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setSelectedTherapist(null)}
              aria-label="Close"
            >
              ✕
            </button>

            {/* Modal Header */}
            <div className="modal-header-hero">
              <img
                src={selectedTherapist.image}
                alt={selectedTherapist.name}
                className="modal-avatar-lg"
              />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#1e1b4b', margin: 0 }}>
                    {selectedTherapist.name}
                  </h3>
                  {selectedTherapist.isVIP ? (
                    <span style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                      color: '#ffffff',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      💎 PRO
                    </span>
                  ) : (
                    <span style={{
                      background: '#ecfdf5',
                      color: '#065f46',
                      border: '1px solid #a7f3d0',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      🌱 Free Essential
                    </span>
                  )}
                </div>
                <div style={{ color: '#6d28d9', fontWeight: 700, fontSize: '0.92rem', marginTop: '4px' }}>
                  {selectedTherapist.specialty}
                </div>
                <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '2px' }}>
                  {selectedTherapist.qualification}
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginTop: '10px', fontSize: '0.85rem' }}>
                  <span style={{ background: '#fef9c3', color: '#854d0e', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                    ★ {selectedTherapist.rating} ({selectedTherapist.reviewCount} Sessions)
                  </span>
                  <span style={{ fontWeight: 600, color: '#334155' }}>
                    ⏱️ {selectedTherapist.experience}+ Years Practice
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="modal-body-content">
              {/* Overview & Philosophy */}
              <div style={{ marginBottom: '22px' }}>
                <h5 className="modal-section-title">About the Practitioner</h5>
                <p style={{ color: '#4b5563', lineHeight: '1.7', fontSize: '0.92rem', margin: 0 }}>
                  {selectedTherapist.bio}
                </p>
              </div>

              {/* Therapeutic Approach */}
              <div style={{ marginBottom: '22px' }}>
                <h5 className="modal-section-title">Therapeutic Approach & Modalities</h5>
                <p style={{ color: '#4b5563', lineHeight: '1.6', fontSize: '0.92rem', background: '#faf5ff', padding: '14px 18px', borderRadius: '12px', border: '1px solid #f3e8ff', margin: 0 }}>
                  🌿 {selectedTherapist.approach}
                </p>
              </div>

              {/* Areas of Clinical Focus */}
              <div style={{ marginBottom: '22px' }}>
                <h5 className="modal-section-title">Areas of Clinical Focus</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedTherapist.concerns.map((concern, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: '#f1f5f9',
                        color: '#1e293b',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.83rem',
                        fontWeight: 600,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      ✓ {concern}
                    </span>
                  ))}
                </div>
              </div>

              {/* Practical Session Info */}
              <div className="row g-3" style={{ background: '#f8fafc', padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>📍 Location:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{selectedTherapist.location}</div>
                </div>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>💰 Session Fee:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#059669' }}>₹{selectedTherapist.consultationFee || 999} for {selectedTherapist.consultationDuration || 50} min</div>
                </div>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>🗣️ Spoken Languages:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{selectedTherapist.languages.join(', ')}</div>
                </div>
                <div className="col-sm-6">
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>⏱️ Session Duration:</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{selectedTherapist.consultationDuration || 50} Minutes (1-on-1)</div>
                </div>
              </div>

              {/* Available Date & Time Slots Section */}
              <div style={{ marginTop: '20px' }}>
                <SlotAvailabilityPicker
                  slots={selectedTherapist.availableSlots || []}
                  weeklySchedule={selectedTherapist.weeklySchedule || null}
                  consultationDuration={selectedTherapist.consultationDuration || 50}
                  onSelectSlot={(slot) => handleBookSession(selectedTherapist, null, slot)}
                  title="Check available slots"
                />
              </div>
            </div>

            {/* Modal Footer CTA */}
            <div className="modal-footer-cta">
              <button
                type="button"
                onClick={() => setSelectedTherapist(null)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.92rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>

              <button
                type="button"
                className="btn-book-session"
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  fontSize: '1rem',
                  borderRadius: '12px',
                  border: 'none'
                }}
                onClick={(e) => handleBookSession(selectedTherapist, e)}
              >
                📅 Book Appointment with {selectedTherapist.rawName} →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🎬 Pro Video Player Modal */}
      {activeVideoModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setActiveVideoModal(null)}
        >
          <div
            style={{
              background: '#0f172a',
              borderRadius: '20px',
              maxWidth: '720px',
              width: '100%',
              overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
              border: '1px solid #334155',
              color: '#ffffff'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #1e293b'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img
                  src={activeVideoModal.image}
                  alt={activeVideoModal.name}
                  style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #a855f7' }}
                />
                <div>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#f8fafc' }}>
                    {activeVideoModal.name}
                  </h4>
                  <div style={{ fontSize: '0.78rem', color: '#c084fc', fontWeight: 600 }}>
                    💎 Pro Practitioner Introduction Video
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveVideoModal(null)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#fff',
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Video Player */}
            <div style={{ background: '#000', position: 'relative', minHeight: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {activeVideoModal.introVideoUrl.includes('youtube.com') || activeVideoModal.introVideoUrl.includes('youtu.be') ? (
                <iframe
                  src={activeVideoModal.introVideoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/') + '?autoplay=1'}
                  title={`${activeVideoModal.name} Video`}
                  style={{ width: '100%', height: '380px', border: 'none', display: 'block' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : activeVideoModal.introVideoUrl.includes('vimeo.com') ? (
                <iframe
                  src={activeVideoModal.introVideoUrl.replace('vimeo.com/', 'player.vimeo.com/video/') + '?autoplay=1'}
                  title={`${activeVideoModal.name} Video`}
                  style={{ width: '100%', height: '380px', border: 'none', display: 'block' }}
                  allowFullScreen
                />
              ) : (
                <video
                  src={getMediaUrl(activeVideoModal.introVideoUrl)}
                  controls
                  autoPlay
                  playsInline
                  style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block' }}
                />
              )}
            </div>

            {/* Footer / Actions */}
            <div style={{
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
              background: '#0b1120',
              borderTop: '1px solid #1e293b'
            }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                  {activeVideoModal.introVideoTitle || `Learn more about Dr. ${activeVideoModal.rawName}'s clinical approach.`}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    const t = activeVideoModal;
                    setActiveVideoModal(null);
                    handleViewDetails(t, e);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #334155',
                    background: '#1e293b',
                    color: '#e2e8f0',
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    cursor: 'pointer'
                  }}
                >
                  View Profile
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    const t = activeVideoModal;
                    setActiveVideoModal(null);
                    handleBookSession(t, e);
                  }}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    cursor: 'pointer'
                  }}
                >
                  Book Session →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TherapistsPage;
